import { logger } from '@adornis/base/logging.js';
import { useEffect, useRef } from 'react';
import { startStructureExtractionSubscription } from './start-structure-extraction.js';

export interface ExtractionHandlers {
  onEvent?: (message?: any) => void | Promise<void>;
  onLowConfidence?: () => void | Promise<void>;
}

export function useExtractionSubscription(
  examId: string | null | undefined,
  conversationId: string | null | undefined,
  handlers: ExtractionHandlers = {},
) {
  const subRef = useRef<{ unsubscribe: () => void } | null>(null);
  useEffect(() => {
    const { onEvent, onLowConfidence } = handlers;
    if (!examId || !conversationId) return;

    try {
      const sub = startStructureExtractionSubscription(examId, conversationId)().subscribe({
        next: raw => {
          try {
            const parsed = JSON.parse(String(raw || '{}'));
            if (parsed.message && typeof onEvent === 'function') {
              void onEvent(parsed.message);
            }
            if (parsed.hasLowConfidence && typeof onLowConfidence === 'function') {
              try {
                void onLowConfidence();
              } finally {
                // end subscription once low-confidence handling triggered (canvas opened)
                try {
                  subRef.current?.unsubscribe();
                } catch (e) {}
              }
            }
          } catch (e) {
            if (typeof onEvent === 'function') void onEvent();
          }
        },
        error: err => {
          logger.error({ err, examId }, 'Extraction change stream encountered an error for exam');
          if (typeof onEvent === 'function') void onEvent();
        },
        complete: () => {},
      });
      subRef.current = { unsubscribe: () => sub.unsubscribe() };
    } catch (e) {
      logger.error({ err: e, examId }, 'Failed to start extraction subscription for exam');
      if (typeof handlers.onEvent === 'function') void handlers.onEvent();
    }

    return () => {
      try {
        subRef.current?.unsubscribe();
      } catch (e) {
        logger.error({ err: e, examId }, 'Failed to unsubscribe from extraction subscription for exam');
      }
      subRef.current = null;
    };
  }, [examId, conversationId, handlers.onEvent, handlers.onLowConfidence]);
}
